"use client"

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import type { RoomType, StyleType, TimePeriodType, SortType } from "./GalleryPage"
import { ROOM_TYPE_MAPPINGS, ROOM_THEME_MAPPINGS } from "@/src/utils/api-value-mappings"

interface GalleryFiltersProps {
  selectedRoom: RoomType
  selectedStyle: StyleType
  selectedTimePeriod: TimePeriodType
  selectedSort: SortType
  onRoomChange: (room: RoomType) => void
  onStyleChange: (style: StyleType) => void
  onTimePeriodChange: (period: TimePeriodType) => void
  onSortChange: (sort: SortType) => void
  onClearFilters: () => void
  isLoggedIn?: boolean
}

const sortedRoomTypes = [...ROOM_TYPE_MAPPINGS].sort((a, b) => a.order - b.order)
const roomOptions = [
  { value: "all", label: "All Rooms" },
  ...sortedRoomTypes.map((room) => ({
    value: room.value,
    label: room.name,
  })),
]

const sortedRoomThemes = [...ROOM_THEME_MAPPINGS].sort((a, b) => a.name.localeCompare(b.name))
const styleOptions = [
  { value: "all", label: "All Styles" },
  ...sortedRoomThemes.map((theme) => ({
    value: theme.value,
    label: theme.name,
  })),
]

const timePeriodOptions = [
  { value: "all", label: "All Time" },
  { value: "month", label: "Last 30 Days" },
  { value: "day", label: "Last Day" },
]

const sortOptions = [
  { value: "created_desc", label: "Newest First" },
  { value: "created_asc", label: "Oldest First" },
  { value: "likes_desc", label: "Most Liked" },
  { value: "my_likes", label: "My Likes Only", requiresAuth: true },
]

export function GalleryFilters({
  selectedRoom,
  selectedStyle,
  selectedTimePeriod,
  selectedSort,
  onRoomChange,
  onStyleChange,
  onTimePeriodChange,
  onSortChange,
  onClearFilters,
  isLoggedIn = false,
}: GalleryFiltersProps) {
  const availableSortOptions = sortOptions.filter((option) => !option.requiresAuth || isLoggedIn)

  return (
    <div className="flex flex-col sm:flex-row gap-4 mb-8">
      <div className="flex-1">
        <Select value={selectedRoom} onValueChange={(value) => onRoomChange(value as RoomType)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select room type" />
          </SelectTrigger>
          <SelectContent>
            {roomOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex-1">
        <Select value={selectedStyle} onValueChange={(value) => onStyleChange(value as StyleType)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select style" />
          </SelectTrigger>
          <SelectContent>
            {styleOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex-1">
        <Select value={selectedTimePeriod} onValueChange={(value) => onTimePeriodChange(value as TimePeriodType)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select time period" />
          </SelectTrigger>
          <SelectContent>
            {timePeriodOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex-1">
        <Select value={selectedSort} onValueChange={(value) => onSortChange(value as SortType)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            {availableSortOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(selectedRoom !== "all" ||
        selectedStyle !== "all" ||
        selectedTimePeriod !== "all" ||
        selectedSort !== "created_desc") && (
        <div className="flex items-center">
          <button onClick={onClearFilters} className="text-sm text-gray-600 hover:text-gray-800 underline font-medium">
            Clear Filters
          </button>
        </div>
      )}
    </div>
  )
}
